import {
    getCredBasicInfo,
    getCredLicenses,
    getCredPayoutAccount,
    getCredVerification,
    getIrsLetter,
    getPayerRequirements,
} from 'api/credentialing';
import type { IrsLetterDto, ProviderCredentialingPayoutAccountDto, TPayerChainContext } from './types';
import { irsLetterComplete, payoutComplete } from './validators';

export type TPayerChainData = {
    ctx: TPayerChainContext;
    // 预填用：本人已存的；group 本人没存过时取 owner（登录用户）的
    irsLetter: IrsLetterDto;
    payout: ProviderCredentialingPayoutAccountDto;
    // Medicaid 问卷用：Verification 的实体类型（operating agreement）与 LICENSE_ACTION 是否答 No（预填）
    entityType?: string;
    licenseActionNo: boolean;
};

const digits = (v?: string) => (v || '').replace(/\D/g, '');

// 原型 needs104Question：Credentials + 执照名里有 NP / APRN / nurse practitioner，且有加州执照
const needs104Question = (basic: any, licenses: any): boolean => {
    const list: any[] = licenses?.practiceLicenses || [];
    const text = [basic?.credential || '', ...list.map((l) => l?.type || '')].join(' ');
    return /NP\b|NP-|APRN|nurse practitioner/i.test(text) && list.some((l) => l?.state === 'CA');
};

const safe = async <T>(fn: () => Promise<any>): Promise<T | null> => {
    try {
        const res: any = await fn();
        return res?.error ? null : (res?.data?.data ?? null);
    } catch (e) {
        return null;
    }
};

// IRS letter / Medicare payout 两步的触发上下文 + 预填数据（宿主算链之前调一次）。
// providerId 为空 = 登录用户本人（solo）；group 时传正在填写的那位 provider。
export const loadPayerChainData = async (
    providerId: number | string | undefined,
    group: boolean,
): Promise<TPayerChainData> => {
    const [verification, ownIrs, payout, basic, licenses, reqs] = await Promise.all([
        safe<any>(() => getCredVerification(providerId)),
        safe<IrsLetterDto>(() => getIrsLetter(providerId)),
        // group 只有一个 practice 账户，不管 header 切到谁都返回它，所以不带 override
        safe<ProviderCredentialingPayoutAccountDto>(() => getCredPayoutAccount()),
        safe<any>(() => getCredBasicInfo(providerId)),
        safe<any>(() => getCredLicenses(providerId)),
        safe<any>(() => getPayerRequirements(providerId)),
    ]);
    let irsLetter: IrsLetterDto = ownIrs || {};
    // group 的 CP 575 是 practice 的同一封：本人没填时用 owner（登录用户）的预填，保存时写进本人名下
    if (group && !irsLetterComplete(irsLetter) && providerId !== undefined) {
        const ownerIrs = await safe<IrsLetterDto>(() => getIrsLetter());
        if (ownerIrs && irsLetterComplete(ownerIrs)) irsLetter = { ...ownerIrs };
    }
    const tin = digits(verification?.taxId);
    const ssn = digits(verification?.ssn);
    const licenseAction = (verification?.disclosures || []).find((d: any) => d?.questionKey === 'LICENSE_ACTION');
    return {
        entityType: verification?.businessEntityType || undefined,
        licenseActionNo: licenseAction?.answer === false,
        ctx: {
            group,
            billsUnderTaxId: group || (tin.length === 9 && tin !== ssn),
            irsLetterOnFile: irsLetterComplete(ownIrs),
            payoutOnFile: payoutComplete(payout),
            needs104: needs104Question(basic, licenses),
            np104: reqs?.caNp104 ?? null,
        },
        irsLetter,
        payout: payout || {},
    };
};
